import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { ethers } from 'ethers'
import { Card, CardBody, CardFooter, Heading } from '@liquidcollectibles/uikit'
import { useTranslation } from 'contexts/Localization'
import { NftMetadata } from 'config/constants/types'
import { getCollectionInfo } from 'state/collectibles/hooks'
import NfaCard from './_components/NfaPreview'
import Preview from './_components/ImgPreview'

export interface NftCardProps {
  nft: NftMetadata
  collectionAddress: string
  canClaim?: boolean
  selectable?: boolean
  tokenIds?: number[]
  isWalletView?: boolean
  footerLabel?: string
  onClaim?: () => Promise<ethers.providers.TransactionResponse>
  refresh?: () => void
  handleSelect?: (nftIndex) => void
}

const licoImageSrc = '/images/lico-token.svg'

const Tags = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`

const CategoryTag = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 8px;
  font-size: 12px;
  background: #424242;
  color: rgba(255, 255, 255, 0.87);
  border-radius: 24px;
`

const AuthorTag = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 2px 6px 2px 2px;
  background: #424242;
  border-radius: 24px;
  margin-right: 12px;
`

const AuthorLogo = styled.div<{ backgroundImage?: string }>`
  height: 19px;
  width: 19px;
  margin-right: 6px;
  background-image: url('${(props) => props.backgroundImage || licoImageSrc}');
  background-size: cover;
  background-position: center;
`
const AuthorName = styled.h3`
  color: rgba(255, 255, 255, 0.87);
  font-size: 12px;
`

const Details = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const NftCard: React.FC<NftCardProps> = ({
  collectionAddress,
  nft,
  tokenIds = [],
  isWalletView = false,
  handleSelect,
  selectable,
  footerLabel,
}) => {
  const [isSelected, setIsSelected] = useState(false)
  const collection = getCollectionInfo(collectionAddress)
  const { t } = useTranslation()
  const { tokenId, name } = nft
  const walletOwnsNft = tokenIds?.indexOf(tokenId) !== -1

  useEffect(() => {
    setIsSelected(false)
  }, [selectable])

  const handleSelected = () => {
    if (!selectable || !handleSelect) return
    setIsSelected(!isSelected)
    handleSelect(nft.tokenId)
  }

  return (
    <Card
      isSuccess={isSelected}
      onClick={handleSelected}
      style={{ border: '1px solid rgb(66, 66, 66)', borderRadius: 6 }}
    >
      {collection.nftAddress === '0x6afc012783e3a6ef8c5f05f8eee2edef6a052ec4' ? (
        <NfaCard nft={nft} rarityTier={nft.attributes[7]?.value as number} borderRadius="6px" />
      ) : (
        <Preview nft={nft} collection={collection} />
      )}
      <CardBody p="16px">
        {isWalletView ? (
          <Tags>
            <AuthorTag>
              <AuthorLogo
                backgroundImage={
                  collection.category === 'Original'
                    ? licoImageSrc
                    : `/images/tokens/${collection.vaultTokenAddress}.png`
                }
              />
              <AuthorName>{collection.author}</AuthorName>
            </AuthorTag>
            <CategoryTag>{collection.category}</CategoryTag>
          </Tags>
        ) : walletOwnsNft ? (
          <Tags>
            <CategoryTag>{t('In Wallet')}</CategoryTag>
          </Tags>
        ) : (
          <Tags>
            <AuthorTag>
              <AuthorLogo
                backgroundImage={
                  collection.category === 'Original'
                    ? licoImageSrc
                    : `/images/tokens/${collection.vaultTokenAddress}.png`
                }
              />
              <AuthorName>{collection.author}</AuthorName>
            </AuthorTag>
          </Tags>
        )}
        <Heading>{name}</Heading>
      </CardBody>
      {footerLabel && (
        <CardFooter p="24px">
          <Details>
            <Link to={`/collection/${collectionAddress}/token/${tokenId}`}>{footerLabel}</Link>
          </Details>
        </CardFooter>
      )}
    </Card>
  )
}

export default NftCard
